import { z, type ZodTypeAny } from 'zod';
import type {
  IElement,
  IField,
  IFormBuilderSchema,
  IFormBuilderValidator,
  IMessages,
  ISection,
  IWMSResponse,
  TElementType,
  TInputType,
  TTemplateName,
} from '~/interfaces/form/formBuilder';
import { generateTranslationKey } from '~/utilities/data-transformers';
import { assignIdNumberValidator } from '~/utilities/form-builder.reg';
import { useServices } from '~/composables/useServices';
import { captchaV3SiteKey } from 'assets/data/config';
import dayjs, { type Dayjs } from 'dayjs';

const AGE_CONSTRAINTS = {
  MIN_AGE_YEARS: 18,
  MAX_AGE_YEARS: 101,
} as const;

const FIELD_TYPES = {
  CHECKBOX: 'checkbox',
  DROPDOWN: 'dropdown',
  RADIO_GROUP: 'radio group',
  RECAPTCHA_V3: 'recapcha v3',
  TOGGLE: 'toggle',
  INPUT: 'input',
  TEXTAREA: 'text area',
} as const;

const INPUT_TYPES = {
  CALENDAR: ['calendar', 'calender'], // Handle spelling error from WMS
  NUMBER: 'number',
  EMAIL: 'email',
  TEXT: 'text',
  PASSWORD: 'password',
  TEL: 'tel',
} as const;

const currentForm = ref<TTemplateName | null>(null);

const createRegexTester = (() => {
  const cache = new Map<string, RegExp>();
  return (value: string, regex: string): boolean => {
    if (!cache.has(regex)) {
      cache.set(regex, new RegExp(regex));
    }
    return cache.get(regex)!.test(value);
  };
})();

const createAgeConstraints = () => {
  const maxAge = dayjs().subtract(AGE_CONSTRAINTS.MIN_AGE_YEARS, 'year');
  const minAge = dayjs().subtract(AGE_CONSTRAINTS.MAX_AGE_YEARS, 'year');
  return { minAge, maxAge };
};

const getInputType = (field: IField): TInputType => {
  const fieldType = field.fieldType.toLowerCase();
  const inputType = field.inputType.toLowerCase() as TInputType;
  const fieldId = field.fieldIdentifier.toLowerCase();
  if (fieldId === 'referralcode') return 'dropDownTextInput';
  if (fieldType === FIELD_TYPES.CHECKBOX) return 'checkbox';
  if (inputType === INPUT_TYPES.NUMBER) return 'tel';
  if (fieldType === FIELD_TYPES.TEXTAREA) return 'textarea';
  if (inputType === INPUT_TYPES.EMAIL) return 'email';
  if (inputType === INPUT_TYPES.PASSWORD) return 'password';
  if (fieldType === FIELD_TYPES.DROPDOWN) return 'dropdown';
  if (INPUT_TYPES.CALENDAR.includes(inputType as 'calendar' | 'calender'))
    return 'calendar';
  return inputType;
};

const createOptionMapper = <T extends { value: string; text: string }>(
  items: T[],
) => {
  const { $t } = useNuxtApp();
  return items.map(({ value, text }) => ({
    value,
    label: $t(text),
  }));
};

const getOptions = (field: IField, fieldType: string) => {
  return createOptionMapper(
    fieldType.toLowerCase() === FIELD_TYPES.DROPDOWN
      ? field.fieldDropdowns
      : field.fieldRadioGroup,
  );
};

const processValidationMessages = (
  validationMessage: string,
  fieldIdentifier: string,
): { messages: IMessages; rules: string[] } => {
  const rules = validationMessage.split('|');
  const messages = Object.fromEntries(
    rules.map((validationType) => [
      validationType,
      `validation.${generateTranslationKey(fieldIdentifier)}.${validationType}`,
    ]),
  ) as IMessages;

  return { messages, rules };
};

const createElement = (field: IField): IElement => {
  const { messages, rules } = processValidationMessages(
    field.validationMessage,
    field.fieldIdentifier,
  );

  const { minAge, maxAge } = createAgeConstraints();

  return {
    ...field,
    messages,
    rules,
    type: field.fieldType.toLowerCase() as TElementType,
    inputType: getInputType(field),
    disabled: field.isReadOnly,
    required: field.isRequired,
    placeholder: field.fieldPlaceholder,
    name: field.fieldIdentifier,
    label: field.fieldLabel,
    id: field.fieldIdentifier,
    regex: field.fieldRegex,
    items: getFieldItems(field),
    props: createFieldProps(field, { minAge, maxAge }),
  };
};

const getFieldItems = (field: IField) => {
  const fieldType = field.fieldType.toLowerCase();
  if (
    fieldType === FIELD_TYPES.DROPDOWN ||
    fieldType === FIELD_TYPES.RADIO_GROUP
  ) {
    return getOptions(field, fieldType);
  }
  return [];
};

const createFieldProps = (
  field: IField,
  ageConstraints: { minAge: Dayjs; maxAge: Dayjs },
) => {
  const props: Record<string, string | number | Dayjs | boolean> = {};

  if (field.fieldIdentifier === 'idNumber') {
    props.defaultValue = 'idNumber';
  }

  if (field.fieldIdentifier === 'dateOfBirth') {
    props.maxCalDate = ageConstraints.minAge;
    props.minCalDate = ageConstraints.maxAge;
    if (field.isReadOnly) {
      props.disableCal = true;
    }
  }

  return props;
};

const processSection = (
  section: ISection,
  formSchema: Ref<IFormBuilderSchema>,
): void => {
  const filteredRows = section.rows.filter((row) => {
    const hasRecaptcha = row.fields.some(
      (field) =>
        field.fieldType.toLowerCase() === FIELD_TYPES.RECAPTCHA_V3 &&
        field.isVisible,
    );

    if (hasRecaptcha) {
      formSchema.value.hasRecaptcha = true;
    }

    return row.fields.some(
      (field) =>
        field.isVisible &&
        field.fieldType.toLowerCase() !== FIELD_TYPES.RECAPTCHA_V3,
    );
  });

  const sectionMapping = {
    id: section.formSectionId,
    sectionTitle: section.formSectionTitle,
    sectionSubtitle: section.formSectionSubTitle,
    elementMapping: {
      id: section.formSectionId,
      elements: filteredRows.map((row) => createElement(row.fields[0])),
    },
  };

  formSchema.value.schema.sectionMapping.push(sectionMapping);
};

const generateSchema = (
  form: IWMSResponse,
  formSchema: Ref<IFormBuilderSchema>,
): void => {
  const { sections } = form;

  formSchema.value.schema.hasSteps = sections.length > 1;
  formSchema.value.isEmpty = sections.length === 0;

  const sectionsToProcess = formSchema.value.schema.hasSteps
    ? [...sections].sort(
        (sectionA, sectionB) =>
          sectionA.formSectionOrder - sectionB.formSectionOrder,
      )
    : sections;

  sectionsToProcess.forEach((section) => processSection(section, formSchema));

  const hasElements = formSchema.value.schema.sectionMapping.some(
    (section) => section.elementMapping.elements.length > 0,
  );

  if (!hasElements) {
    formSchema.value.isEmpty = true;
  }
};

const createZodValidator = (element: IElement, formTitle: TTemplateName) => {
  const validators = {
    dropDownTextInput: () =>
      z.string().refine((value) => createRegexTester(value, element.regex), {
        message: `validation.${generateTranslationKey(element.name)}.regex`,
      }),
    dropdown: () =>
      z.string().refine((value) => createRegexTester(value, element.regex), {
        message: `validation.${generateTranslationKey(element.name)}.regex`,
      }),
    password: () =>
      z.string().refine((value) => createRegexTester(value, element.regex), {
        message: `validation.${generateTranslationKey(element.name)}.regex`,
      }),
    tel: () =>
      z.string().refine((value) => createRegexTester(value, element.regex), {
        message: 'validation.username.regex',
      }),
    calendar: () =>
      z.string().refine((value) => createRegexTester(value, element.regex), {
        message: 'validation.calendar.regex',
      }),
    email: () =>
      z
        .string()
        .email()
        .refine((value) => createRegexTester(value, element.regex), {
          message: `validation.${generateTranslationKey(element.name)}.regex`,
        }),
    checkbox: () =>
      z
        .boolean()
        .refine((value) => createRegexTester(String(value), element.regex), {
          message: `validation.${generateTranslationKey(element.name)}.regex`,
        }),
    default: () => {
      if (element.type === FIELD_TYPES.TOGGLE) {
        return validators.checkbox();
      }
      if (
        (element.id === 'idNumber' && formTitle === 'registration') ||
        (element.id === 'idNumber' && formTitle === 'complete registration')
      ) {
        return assignIdNumberValidator(element.regex);
      }
      return z
        .string()
        .refine((value) => createRegexTester(value, element.regex), {
          message: `validation.${generateTranslationKey(element.name)}.regex`,
        });
    },
  };
  const validator =
    validators[element.inputType as keyof typeof validators] ||
    validators.default;
  return validator();
};

const generateValidator = (
  formSchema: Ref<IFormBuilderSchema>,
): { [p: string]: ZodTypeAny } => {
  if (formSchema.value.isEmpty) return undefined;

  const validators = new Map<string, ZodTypeAny>();

  formSchema.value.schema.sectionMapping.forEach((section) => {
    section.elementMapping.elements.forEach((element) => {
      validators.set(
        element.id,
        createZodValidator(element, formSchema.value.formTitle!),
      );
    });
  });

  return Object.fromEntries(validators);
};

const handleFormError = (templateName: TTemplateName, error?: never) => {
  const errorMessage = `No form returned using template name ${templateName}, either the form is empty doesn't exist or another issue occurred.`;

  if (import.meta.env.DEV) {
    console.error(errorMessage, error);
  }
};

export default async (
  templateName: TTemplateName,
): Promise<{
  formSchema: Ref<IFormBuilderSchema>;
  schemaValidator: Ref<IFormBuilderValidator>;
}> => {
  const settings = useSiteStore();
  currentForm.value = templateName;
  const formSchema = ref<IFormBuilderSchema>({
    isEmpty: true,
    schema: {
      hasSteps: false,
      sectionMapping: [],
    },
    formSubTitle: '',
    formTitle: null,
    hasRecaptcha: false,
  });

  const schemaValidator = ref<IFormBuilderValidator>();

  try {
    const { $formService } = useServices();
   const form = await $formService.fetchForm(templateName);
   if (!form || typeof form !== 'object' || Object.keys(form).length === 0) {
      handleFormError(templateName);
      return { formSchema, schemaValidator };
    }

    if (!form.sections || form.sections.length < 1) {
      handleFormError(templateName);
      return { formSchema, schemaValidator };
    }

    formSchema.value.formTitle = form.formTitle;
    formSchema.value.formSubTitle = form.formSubTitle;

    generateSchema(form, formSchema);
    schemaValidator.value = generateValidator(formSchema);

    if (formSchema.value.hasRecaptcha) {
      useHead({
        script: [
          {
            src: `https://www.google.com/recaptcha/api.js?render=${captchaV3SiteKey[settings.getEnvironment]}`,
          },
        ],
      });
    }
  } catch (error) {
    handleFormError(templateName, error as never);
  }

  return { formSchema, schemaValidator };
};

export const testRegex = createRegexTester;
